import { CachePolicies, useFetch } from 'use-http';
import { routes } from '@/webapi/routes';
import { Status } from '@/webapi/use-auth-api';

export interface DailyPriceTestReport {
  days: DailyPriceTestDay[];
}

export interface DailyPriceTestDay {
  date: string;
  variants: DailyPriceTestVariant[];
}

export interface DailyPriceTestVariant {
  useCaseVariant: string;
  sessions: number;
  atcRate: number;
  psv: number;
  returns: number;
  returnsRate: number;
  itemsSold: number;
  otw: number;
  revenue: number;
  profit: number;
  profitPerSession: number;
  profitRate: number;
  pdv: number;
  transactions: number;
}

export interface PriceTestOptions {
  experienceId: string;
  goal?: string;
  init: boolean;
  version: number;
  expEndDate?: Date;
  startAt?: Date;
  endAt?: Date;
  sessionType?: string;
  audience?: string;
  activeVariants: string[];
}

export interface PriceTestResponse {
  products: PriceTestProductSummary[];
  total: number;
}

export interface PriceTestProductSummary {
  date: Date;
  useCase: string;
  audience: string;
  title: string;
  image: string;
  source: string;
  version: number;
  pid: number;
  variants: PriceTestProductVariation[];
  totalRevenue: number;
  totalItemsSoled: number;
}

export interface PriceTestProductVariation {
  useCaseVariant: string;
  sessions: number;
  atcRate: number;
  psv: number;
  returns: number;
  returnsRate: number;
  itemsSold: number;
  otw: number;
  revenue: number;
  profit: number;
  profitPerSession: number;
  profitRate: number;
  pdv: number;
  transactions: number;
  isControl?: boolean;
}

export function usePriceTestReportApi() {
  const { post, put, error, loading } = useFetch(``, (globalOptions) => ({
    ...globalOptions,
    ...{
      credentials: `include`,
      cachePolicy: CachePolicies.NO_CACHE,
      loading: true,
    },
  }));

  function getReportData(
    page: number,
    size: number,
    args: PriceTestOptions,
  ): Promise<PriceTestResponse> {
    if (!args.init) {
      return Promise.resolve({
        total: 0,
        products: [],
      } as PriceTestResponse);
    }
    return post(routes.priceTestSummary, { page, size, ...args });
  }

  function getDailyReportData(
    args: PriceTestOptions,
  ): Promise<DailyPriceTestReport> {
    if (!args.init) {
      return Promise.resolve({
        days: [],
      } as DailyPriceTestReport);
    }
    return post(routes.priceTestSummaryDaily, args);
  }

  function setReportType(
    b: boolean,
    expId: string,
  ): Promise<DailyPriceTestReport> {
    return put(routes.saveExperienceSessionsType, {
      priceTestReport: b ? Status.YES : Status.NO,
      experienceId: expId,
    });
  }

  function priceTestPrices(productIds: number[], expId: string) {
    return post(routes.priceTestPrices, {
      productIds,
      expId,
    });
  }

  return {
    loading,
    error,
    priceTestPrices,
    getDailyReportData,
    getReportData,
    setReportType,
  };
}
